import { Box, Grid, styled } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'
import { ReportProblemOutlined as WarningIcon } from '@mui/icons-material'
import Bar from './Bar'
import { Typography } from '@carto/meridian-ds/components'

interface QuotasStatsBarChartProps {
  current?: number | undefined
  limit?: number | undefined
  labelId: string
  descriptionHidden?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  descriptionValues?: any
}

const Root = styled(Grid)(({ theme }) => ({
  flexDirection: 'column',

  '& + &': {
    marginTop: theme.spacing(1.75)
  }
}))

const LabelsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: 'inherit',
  marginBottom: theme.spacing(0.5)
}))

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  '& svg': {
    marginRight: theme.spacing(0.5),
    color: theme.palette.warning.main
  }
}))

function QuotasStatsBarChart({
  current = 0,
  limit = 0,
  labelId,
  descriptionHidden,
  descriptionValues
}: QuotasStatsBarChartProps) {
  const hasReachedLimits = current > limit
  const activityDataEnabled = useFeatureFlag(FeatureFlag.DeliverRawAuditLogs)
  const legacyCopy = !activityDataEnabled && labelId === 'usage'

  return (
    <Root item container xs={12}>
      <LabelsWrapper>
        <Title variant="body2" weight="strong">
          {hasReachedLimits && <WarningIcon fontSize="small" />}
          <FormattedMessage id={`account.cards.stats.chart.${labelId}.label`} />
        </Title>
        <Typography variant="body2">
          <FormattedMessage
            id="account.cards.stats.chart.indicator"
            values={{
              current: current?.toLocaleString() || '-',
              max: limit?.toLocaleString() || '-'
            }}
          />
        </Typography>
      </LabelsWrapper>
      <Bar current={current} limit={limit} />
      {!descriptionHidden && (
        <Box pt={0.75}>
          <Typography component="p" variant="caption" color="textSecondary" weight="regular">
            <FormattedMessage
              id={`account.cards.stats.chart.${labelId}.description${legacyCopy ? 'WithoutActivity' : ''}`}
              values={descriptionValues}
            />
          </Typography>
        </Box>
      )}
    </Root>
  )
}

export default QuotasStatsBarChart
